<template>
  <div class="order-list-filtered-table">
    <FilterFrame
      ref="filter"
      :filter-form="orderFoodRecordsFilter"
      @search="onSearch"
      @clear="firstSearch"
    />

    <div class="data-table__title">
      <div>
        <span class="data-table__count"> {{ `${$t('OrderFoodRecord.SearchResult')} ${totalCount} ${$t('OrderFoodRecord.SearchResultCount')}` }} </span>
        <el-button round @click="exportRecord">
          {{ $t('OrderFoodRecord.Export') }}
        </el-button>
      </div>
    </div>

    <el-table :data="orderRecords">
      <el-table-column :label="$t('OrderFoodManagement.TableLabelOrderNumber')" min-width="100">
        <template slot-scope="scope">
          {{ scope.row.displayComOrderId || '-' }}
        </template>
      </el-table-column>
      <el-table-column :label="$t('OrderFoodManagement.TableLabelOrderCreateTime')" prop="displayCreatedAtUtc" min-width="170"></el-table-column>
      <el-table-column :label="$t('OrderFoodManagement.TableLabelDiningMode')" min-width="80">
        <template slot-scope="scope">
          {{ scope.row.diningModeText || '-' }}
        </template>
      </el-table-column>
      <el-table-column :label="$t('OrderFoodManagement.TableLabelDiningModeInfo')" min-width="120">
        <template slot-scope="scope">
          {{ scope.row.diningModeInfo || '-' }}
        </template>
      </el-table-column>
      <el-table-column :label="$t('OrderFoodManagement.TableLabelFoodItems')" min-width="250">
        <template slot-scope="scope">
          <div v-html="scope.row.itemsDisplayHtml"></div>
        </template>
      </el-table-column>
      <el-table-column :label="$t('OrderFoodRecord.TableLabelHousehold')" prop="tabletNote" min-width="130"></el-table-column>
      <el-table-column
        :label="$t('OrderFoodRecord.TableLabelSystemUser')"
        prop="updatedUser"
        min-width="100"
      ></el-table-column>
      <el-table-column :label="$t('OrderFoodRecord.TableLabelStatus')" min-width="100">
        <template slot-scope="scope">
          <template v-if="listKind === 'successful'">
            <span class="data-table__successful">{{ $t('OrderFoodRecord.FilterStatusSuccessful') }}</span>
          </template>
          <template v-else>
            <span
              :class="(scope.row.voidedStatus || scope.row.cancelInfoClickable) ? 'data-table__void' : ''"
              @click="scope.row.voidedStatus
                ? $emit('show-void-info', scope.row)
                : scope.row.cancelInfoClickable && $emit('show-cancel-info', scope.row)"
            >
              {{ scope.row.stateName }}
            </span>
          </template>
        </template>
      </el-table-column>
      <el-table-column v-if="showActionColumn" :label="$t('OrderFoodRecord.TableLabelAction')" :width="listKind === 'successful' ? 180 : 100">
        <template slot-scope="scope">
          <div class="operate">
            <template v-if="listKind === 'successful'">
              <el-tooltip
                :disabled="scope.row.operationStatus || scope.row.voidStatus"
                :content="$t('OrderFoodRecord.OvertimeVoidTip')"
                placement="top"
              >
                <div>
                  <el-button
                    size="mini"
                    type="danger"
                    plain
                    :disabled="!scope.row.operationStatus"
                    @click="$emit('void-order', scope.row)"
                  >
                    {{ $t('OrderFoodRecord.Void') }}
                  </el-button>
                </div>
              </el-tooltip>
              <el-tooltip
                :disabled="scope.row.operationStatus"
                :content="scope.row.voidStatus ? $t('OrderFoodRecord.VoidTip') : $t('OrderFoodRecord.OvertimeReprintTip')"
                placement="top"
              >
                <div>
                  <el-button
                    size="mini"
                    type="success"
                    class="reprint-btn"
                    :disabled="!scope.row.operationStatus"
                    @click="$emit('reprint-order', scope.row)"
                  >
                    {{ $t('OrderFoodRecord.RePrint') }}
                  </el-button>
                </div>
              </el-tooltip>
            </template>
            <template v-else>
              <el-button
                v-if="!scope.row.isUserCanceled"
                size="mini"
                type="success"
                @click="$emit('reprint-order', scope.row)"
              >
                {{ $t('OrderFoodRecord.RePrint') }}
              </el-button>
            </template>
          </div>
        </template>
      </el-table-column>
    </el-table>
    <TableLoading :is-loading="loading" />
  </div>
</template>

<script>
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import TableLoading from '@/components/TableLoading'
import FilterFrame from '@/components/FilterFrame.vue'
import { apiOrderFood } from '@/api/point/orderFood.js'
import { apiOrderFood as apiOrderFoodNew } from '@/api/OrderFood'
import { convertKeysToPascalCase, convertKeysToCamelCase } from '@/js/utils/bff'
import { getDiningInfoText, getDiningOptionLabel, formatOrderDisplayCode, isCanceledState, CanceledOrderState } from '@/js/utils/orderFood'

dayjs.extend(utc)

/** 與 GET TableFood/Form 系列 API 查詢參數一致之列表種類 */
const LIST_KIND_SUCCESSFUL = 'successful'
const LIST_KIND_CANCELED_VOID = 'canceledVoid'

export default {
  name: 'OrderListFilteredTable',
  components: {
    FilterFrame,
    TableLoading
  },
  props: {
    listKind: {
      type: String,
      required: true,
      validator: (v) => [LIST_KIND_SUCCESSFUL, LIST_KIND_CANCELED_VOID].includes(v)
    },
    showActionColumn: {
      type: Boolean,
      default: false
    },
    isActive: {
      type: Boolean,
      default: false
    },
    refreshSeed: {
      type: Number,
      default: 0
    }
  },
  data () {
    const orderRecords = []
    return {
      initialized: false, // 是否已初始化
      filter: {}, // 篩選條件
      loading: false, // 是否正在加載
      manualSearchProcess: false, // 是否正在手動搜尋
      scrollEnd: false, // 是否已到達最後一頁
      maxCount: 10, // 每頁最大數量
      offset: 0, // 偏移量
      orderFoodRecordsFilter: [], // 篩選器
      orderRecords, // 訂單列表
      totalCount: 0 // 總數量
    }
  },
  watch: {
    isActive: {
      immediate: true,
      handler (val) {
        if (val) {
          if (this.initialized) {
            this.reloadCurrentTab()
          } else {
            this.ensureLoaded()
          }
          window.addEventListener('scroll', this.scrollToLoad, true)
        } else {
          window.removeEventListener('scroll', this.scrollToLoad, true)
        }
      }
    },
    refreshSeed () {
      if (this.isActive && this.initialized) {
        this.reloadCurrentTab()
      }
    }
  },
  beforeDestroy () {
    window.removeEventListener('scroll', this.scrollToLoad, true)
  },
  methods: {
    /**
     * 根據選擇器構建含時區的本地日期範圍（UTC+8）
     * @param {Date} dateStart 開始日期
     * @param {Date} dateEnd 結束日期
     * @param {string} dateRadio 日期類型
     * @returns {Object} 包含 startDateTime 和 endDateTime 的物件
     */
    buildUtcRangeFromPicker (dateStart, dateEnd, dateRadio) {
      const fmt = 'YYYY-MM-DDTHH:mm:ssZ'
      let startD
      let endD
      if (dateRadio === '2') {
        startD = dayjs(dateStart).utcOffset(8).startOf('month')
        endD = dayjs(dateEnd).utcOffset(8).startOf('month').add(1, 'month')
      } else {
        startD = dayjs(dateStart).utcOffset(8).startOf('day')
        endD = dayjs(dateEnd).utcOffset(8).startOf('day').add(1, 'day')
      }
      return {
        startDateTime: startD.format(fmt),
        endDateTime: endD.format(fmt)
      }
    },
    /**
     * 格式化項目列表為純文字
     * @param {Array} items 項目列表
     * @returns {string} 純文字格式化的項目列表
     */
    formatItemsPlain (items) {
      if (!items || !items.length) return ''
      return items.map((i) => {
        const group = i.groupName || ''
        const name = i.itemName || ''
        const q = i.quantity != null ? i.quantity : 0
        return `【${group}】 ${name}:${q}`
      }).join('\n')
    },
    /**
     * 正規化表單資料
     * @param {Object} form 表單資料
     * @returns {Object} 正規化後的表單資料
     */
    normalizeFormRow (form) {
      const items = form.items || [] // 餐點品項列比
      const itemsPlain = this.formatItemsPlain(items)
      const itemsDisplayHtml = itemsPlain.replace(/\n/g, '<br/>')
      const dining = form.dining || {} // 用餐方式
      const canceledStatus = form.canceledStatus === true || isCanceledState(form.state) // 是否為取消
      const voidedStatus = form.voidedStatus === true // 是否為作廢
      const cancelInfoClickable = canceledStatus && form.state !== CanceledOrderState.UserCanceled // 取消類可點擊（住戶取消不開放查看備註）
      return {
        ...form,
        comOrderId: form.comOrderId, // 訂單編號
        displayComOrderId: formatOrderDisplayCode(form.comOrderId, form.createdAtUtc), // 顯示訂單編號 MMDD + 尾部四碼
        updatedUser: form.updatedUser, // 更新人員
        itemsPlain, // 餐點品項列表
        itemsDisplayHtml, // 餐點品項列表(html格式)
        displayCreatedAtUtc: form.createdAtUtc ? dayjs(form.createdAtUtc).format('YYYY/MM/DD HH:mm') : '-', // 訂單建立時間
        diningModeText: getDiningOptionLabel(dining.diningOption), // 用餐方式文字
        diningModeInfo: getDiningInfoText(dining), // 用餐方式資訊
        canceledStatus, // 是否取消
        cancelInfoClickable, // 取消類可點擊（住戶取消不開放查看備註）
        isUserCanceled: form.state === CanceledOrderState.UserCanceled, // 是否為住戶取消
        voidedStatus, // 是否作廢
        voidStatus: voidedStatus, // 舊欄位相容，僅作廢才為 true
        operationStatus: form.operationStatus !== undefined && form.operationStatus !== null ? form.operationStatus : true, // 操作狀態
        paymentMode: form.paymentMode !== undefined ? form.paymentMode : 1 // 支付方式
      }
    },
    /**
     * 提取列表載荷
     * @param {Object} converted 轉換後的資料
     * @returns {Object} 包含 forms 和 totalCount 的物件
     */
    extractListPayload (converted) {
      if (converted.status !== 1) return { forms: [], totalCount: 0 }
      const data = converted.data
      if (data && Array.isArray(data.forms)) {
        return { forms: data.forms, totalCount: data.totalCount != null ? data.totalCount : 0 }
      }
      if (Array.isArray(converted.forms)) {
        return { forms: converted.forms, totalCount: converted.totalCount != null ? converted.totalCount : 0 }
      }
      return { forms: [], totalCount: 0 }
    },
    /**
     * 確保已加載
     * @returns {Promise<boolean>} 是否已加載
     */
    async ensureLoaded () {
      if (this.initialized) return
      await this.initFilter()
      this.initialized = true
      this.firstSearch()
    },
    /**
     * 初始化篩選器
     * @returns {Promise<void>} 初始化完成
     */
    async initFilter () {
      const systemUsersRes = await apiOrderFood.getSystemUserList()
      const systemUsers = systemUsersRes.data.Data.Staffs.map(user => {
        return {
          key: user.AdminId,
          label: user.AdminName,
          value: user.AdminName
        }
      })
      const today = new Date()
      const sevenDaysAgo = dayjs(today).subtract(7, 'day').toDate()
      const dateField = {
        label: this.$t('OrderFoodRecord.FilterLabelDate'),
        name: 'date',
        type: 'dateOrMonthPicker',
        value: {
          date: [sevenDaysAgo, today],
          radio: '1'
        },
        options: [
          {
            label: this.$t('OrderFoodRecord.FilterByDate'),
            value: '1'
          }, {
            label: this.$t('OrderFoodRecord.FilterByMonth'),
            value: '2'
          }
        ]
      }
      const householdField = {
        label: this.$t('OrderFoodRecord.FilterLabelHousehold'),
        name: 'household',
        type: 'house',
        value: ''
      }
      const userField = {
        label: this.$t('OrderFoodRecord.FilterLabelSystenUser'),
        name: 'updatedUser',
        type: 'select',
        clearable: true,
        value: '',
        options: systemUsers
      }
      this.orderFoodRecordsFilter = this.listKind === LIST_KIND_SUCCESSFUL
        ? [dateField, householdField, userField]
        : [dateField, householdField]
    },
    /**
     * 第一次搜尋
     * @returns {void} 搜尋完成
     */
    firstSearch () {
      const today = new Date()
      const sevenDaysAgo = dayjs(today).subtract(7, 'day').toDate()
      const { startDateTime, endDateTime } = this.buildUtcRangeFromPicker(sevenDaysAgo, today, '1')
      this.filter = {
        startDateTime,
        endDateTime,
        tabletId: undefined,
        updatedUser: undefined
      }
      this.offset = 0
      this.scrollEnd = false
      this.orderRecords = []
      this.fetchOrderResult()
    },
    /**
     * 重新加載當前分頁
     * @returns {Promise<void>} 重新加載完成
     */
    async reloadCurrentTab () {
      this.offset = 0
      this.scrollEnd = false
      this.orderRecords = []
      await this.fetchOrderResult()
    },
    /**
     * 獲取訂單結果
     * @returns {Promise<boolean>} 是否獲取成功
     */
    async fetchOrderResult () {
      try {
        // 如果已經載入完成，則不進行查詢
        if (!this.isActive || this.scrollEnd || this.loading) return
        // 如果沒有查詢條件，則不進行查詢
        if (!this.filter || !this.filter.startDateTime || !this.filter.endDateTime) return

        this.loading = true

        // 構建查詢參數
        const baseParams = {
          startDateTime: this.filter.startDateTime,
          endDateTime: this.filter.endDateTime,
          ...(this.filter.tabletId && { tabletId: this.filter.tabletId }),
          ...(this.listKind === LIST_KIND_SUCCESSFUL && this.filter.updatedUser && { updatedUser: this.filter.updatedUser }),
          offset: this.offset,
          maxCount: this.maxCount
        }
        const pascalParams = convertKeysToPascalCase(baseParams)

        /**
         * 查詢訂單結果
         * 1. 依照列表種類查詢
         */
        let res
        if (this.listKind === LIST_KIND_SUCCESSFUL) {
          res = convertKeysToCamelCase((await apiOrderFoodNew.getTableFoodFormSuccessfuls(pascalParams)).data)
        } else {
          res = convertKeysToCamelCase((await apiOrderFoodNew.getTableFoodFormCanceledAndVoid(pascalParams)).data)
        }

        const { forms, totalCount } = this.extractListPayload(res)
        this.totalCount = totalCount

        if (forms.length < this.maxCount) this.scrollEnd = true // 如果結果數量小於每頁最大數量，則設置為已到達最後一頁
        this.offset += this.maxCount // 增加偏移量
        await this.$sleep(300) // 等待 300 毫秒
        const mapped = forms.map(f => this.normalizeFormRow(f)) // 正規化表單資料
        this.orderRecords = [...this.orderRecords, ...mapped] // 更新訂單列表
        return true
      } catch (error) {
        // eslint-disable-next-line no-console
        console.log(error)
        return false
      } finally {
        this.loading = false
      }
    },
    /**
     * 處理搜尋
     * @param {Object} data 搜尋條件
     * @returns {Promise<void>} 搜尋完成
     */
    async onSearch (data) {
      if (this.manualSearchProcess) return
      this.manualSearchProcess = true
      this.offset = 0
      this.scrollEnd = false
      this.orderRecords = []
      if (data) {
        if (data.dateRadio === '2') {
          data.dateDate[1] = new Date(data.dateDate[1].getFullYear(), data.dateDate[1].getMonth() + 1, 0)
        }
        const { startDateTime, endDateTime } = this.buildUtcRangeFromPicker(
          data.dateDate[0],
          data.dateDate[1],
          data.dateRadio
        )
        this.filter = {
          startDateTime,
          endDateTime,
          tabletId: data.household && data.household.TabletId,
          ...(this.listKind === LIST_KIND_SUCCESSFUL && data.updatedUser && { updatedUser: data.updatedUser })
        }
      }
      await this.fetchOrderResult()
      this.manualSearchProcess = false
    },
    /**
     * 處理滾動加載
     * @returns {Promise<void>} 滾動加載完成
     */
    async scrollToLoad () {
      if (!this.isActive || this.manualSearchProcess || !this.initialized) return
      if (!this.filter || !this.filter.startDateTime || !this.filter.endDateTime) return
      this.$handleScrollWithLoading(this.fetchOrderResult)
    },
    /**
     * 構建匯出使用的本地日期範圍
     * @param {Date} dateStart 開始日期
     * @param {Date} dateEnd 結束日期
     * @param {string} dateRadio 日期類型
     * @returns {Object} 包含 startDateTime 和 endDateTime 的物件
     */
    buildLocalRangeForExport (dateStart, dateEnd, dateRadio) {
      const fmt = 'YYYY-MM-DDTHH:mm:ssZ'
      let startD
      let endD
      if (dateRadio === '2') {
        startD = dayjs(dateStart).utcOffset(8).startOf('month')
        endD = dayjs(dateEnd).utcOffset(8).startOf('month').add(1, 'month')
      } else {
        startD = dayjs(dateStart).utcOffset(8).startOf('day')
        endD = dayjs(dateEnd).utcOffset(8).startOf('day').add(1, 'day')
      }
      return {
        startDateTime: startD.format(fmt),
        endDateTime: endD.format(fmt)
      }
    },
    /**
     * 匯出點餐紀錄
     * @returns {Promise<void>} 匯出完成
     */
    async exportRecord () {
      const filterData = this.$refs.filter.selectedValue
      const { startDateTime, endDateTime } = this.buildLocalRangeForExport(
        filterData.dateDate[0],
        filterData.dateDate[1],
        filterData.dateRadio
      )
      const params = {
        startDateTime,
        endDateTime,
        ...(filterData.household && { tabletId: filterData.household.TabletId })
      }
      if (this.listKind === LIST_KIND_SUCCESSFUL) {
        params.updatedUser = filterData.updatedUser || ''
      }
      try {
        const apiRes = this.listKind === LIST_KIND_SUCCESSFUL
          ? await apiOrderFoodNew.exportTableFoodFormSuccessfuls(convertKeysToPascalCase(params))
          : await apiOrderFoodNew.exportTableFoodFormCanceledAndVoid(convertKeysToPascalCase(params))
        const converted = convertKeysToCamelCase(apiRes.data)
        if (converted.status !== 1 || !converted.data || !converted.data.downloadUrl) {
          throw new Error('export failed')
        }
        const link = document.createElement('a')
        link.href = converted.data.downloadUrl
        if (converted.data.fileName) {
          link.setAttribute('download', converted.data.fileName)
        }
        link.target = '_blank'
        link.rel = 'noopener noreferrer'
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
      } catch (error) {
        // eslint-disable-next-line no-console
        console.log(error)
        this.$message.error(this.$t('OrderFoodRecord.ExportFailed'))
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.order-list-filtered-table {
  .subtitle {
    display: flex;
    align-items: center;
    margin-top: 2rem;
    margin-bottom: 1rem;
    i {
      margin-right: 5px;
    }
  }

  .data-table {
    &__title {
      display: flex;
      align-items: center;
      justify-content: flex-end;
      text-align: left;
      margin-top: 2rem;
      margin-bottom: 1rem;
      i {
        margin-right: 0.5rem;
      }
    }
    &__count {
      margin-right: .5rem;
    }
    &__successful {
      color: $mainColor;
    }
    &__void {
      text-decoration: underline;
      cursor: pointer;
      color: $warnColor;
    }
  }

  .operate {
    display: flex;
    .reprint-btn {
      margin-left: 10px;
    }
  }

  ::v-deep .el-table {
    @include basicTable;
    &__body a.el-button--default.is-plain {
      color: #333;
    }
  }
}
</style>
